import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useLazyQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import PageHeader from "designV2/components/PageHeader";
import Panel from "designV2/components/Panel";
import Field from "designV2/components/Field";
import Button from "designV2/components/Button";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import { Skeleton } from "designV2/components/Skeleton";
import { endPoint } from "utils/config";
import { readableError } from "../shopFields";
import "../superShops.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 *
 * createOrderPromotionByAdmin / updateOrderPromotionByAdmin are new:
 * the backend only ever had the ByShopAdmin pair, which read the shop
 * off the caller's own token. A super admin has no shop of their own, so
 * these take it as input. Before that existed this screen had nothing to
 * submit to — the old form posted a shop-admin mutation that a super
 * admin is not permitted to call.
 * ------------------------------------------------------------------ */

const GET_PROMOTION = gql`
  query getOrderPromotionForEdit($_id: ID) {
    getOrderPromotionsByAdmin(pagination: { limit: 1, skip: 0 }, filters: { _id: $_id }) {
      _id
      promotionCode
      condition
      type
      percent
      maximumPromotion
      useLimitCount
      from
      to
      shop {
        _id
        name
      }
    }
  }
`;

const GET_SHOPS = gql`
  query getShopsForPromotionForm($name: String) {
    getShopsByAdmin(pagination: { limit: 20, skip: 0 }, filters: { name: $name }) {
      _id
      name
    }
  }
`;

const CREATE_PROMOTION = gql`
  mutation createOrderPromotionByAdmin($data: CreateOrderPromotionByAdminInput!) {
    createOrderPromotionByAdmin(data: $data) {
      _id
      promotionCode
    }
  }
`;

const UPDATE_PROMOTION = gql`
  mutation updateOrderPromotionByAdmin($data: CreateOrderPromotionByAdminInput!, $promotionId: ID!) {
    updateOrderPromotionByAdmin(data: $data, promotionId: $promotionId) {
      _id
      promotionCode
    }
  }
`;

const TYPE_OPTIONS = [
  { value: "PERCENT", label: "Percentage off" },
  { value: "FIXED", label: "Fixed amount off" },
];
const TYPE_LABELS = TYPE_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const CONDITION_OPTIONS = [
  { value: "TIMELY", label: "Time limited" },
  { value: "FIRST_ORDER", label: "First order only" },
  { value: "PERCENTAGE", label: "Percentage" },
];
const CONDITION_LABELS = CONDITION_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const SEARCH_DEBOUNCE_MS = 350;

const toInputDate = (value) => (value ? moment(value).format("YYYY-MM-DD") : "");

/**
 * ShopPromotionForm - create or edit a shop promotion code.
 *
 * Replaces this screen's use of commonComponents/addPromotion, a
 * 1,185-line form shared with the trip promotions screen that branched on
 * `model` and `theme` props throughout. Shop and trip promotions are
 * different resolvers with different fields, so they are now separate
 * forms rather than one form pretending to be both.
 */
const ShopPromotionForm = ({ promotionId }) => {
  const isEdit = Boolean(promotionId);

  const [shopId, setShopId] = useState("");
  const [shopSearch, setShopSearch] = useState("");
  const [promotionCode, setPromotionCode] = useState("");
  const [type, setType] = useState("PERCENT");
  const [condition, setCondition] = useState("TIMELY");
  const [percent, setPercent] = useState("");
  const [maximumPromotion, setMaximumPromotion] = useState("");
  const [useLimitCount, setUseLimitCount] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: promotionData, loading: loadingPromotion, error: promotionError } = useQuery(
    GET_PROMOTION,
    { variables: { _id: promotionId }, skip: !isEdit, fetchPolicy: "network-only" }
  );

  const [findShops, { data: shopsData }] = useLazyQuery(GET_SHOPS, { fetchPolicy: "network-only" });
  const [createPromotion] = useMutation(CREATE_PROMOTION);
  const [updatePromotion] = useMutation(UPDATE_PROMOTION);

  // Seed the form once the promotion arrives.
  const existing = promotionData && promotionData.getOrderPromotionsByAdmin[0];
  useEffect(() => {
    if (!existing) return;
    setShopId((existing.shop && existing.shop._id) || "");
    setShopSearch((existing.shop && existing.shop.name) || "");
    setPromotionCode(existing.promotionCode || "");
    setType(existing.type || "PERCENT");
    setCondition(existing.condition || "TIMELY");
    setPercent(existing.percent === null || existing.percent === undefined ? "" : String(existing.percent));
    setMaximumPromotion(
      existing.maximumPromotion === null || existing.maximumPromotion === undefined
        ? ""
        : String(existing.maximumPromotion)
    );
    setUseLimitCount(
      existing.useLimitCount === null || existing.useLimitCount === undefined
        ? ""
        : String(existing.useLimitCount)
    );
    setFrom(toInputDate(existing.from));
    setTo(toInputDate(existing.to));
  }, [existing]);

  useEffect(() => {
    const handler = setTimeout(() => {
      findShops({ variables: { name: shopSearch.trim() } });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [shopSearch, findShops]);

  const shopOptions = useMemo(
    () => ((shopsData && shopsData.getShopsByAdmin) || []).map((s) => ({ value: s._id, label: s.name })),
    [shopsData]
  );
  const selectedShop = shopOptions.find((o) => o.value === shopId);
  const selectedShopLabel =
    (selectedShop && selectedShop.label) ||
    (existing && existing.shop && existing.shop._id === shopId ? existing.shop.name : "");

  const validate = () => {
    const next = {};
    if (!shopId) next.shop = "Choose the shop this code belongs to.";
    if (!promotionCode.trim()) next.promotionCode = "A code is required.";
    const value = parseFloat(percent);
    if (percent === "" || Number.isNaN(value) || value <= 0) {
      next.percent = "Enter a discount greater than zero.";
    } else if (type === "PERCENT" && value > 100) {
      next.percent = "A percentage cannot be more than 100.";
    }
    if (useLimitCount !== "" && (parseInt(useLimitCount, 10) <= 0 || Number.isNaN(parseInt(useLimitCount, 10)))) {
      next.useLimitCount = "Use limit must be a whole number above zero.";
    }
    if (from && to && moment(to).isBefore(moment(from))) {
      next.to = "The end date is before the start date.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = (event) => {
    if (event) event.preventDefault();
    if (saving) return;
    setFormError("");
    if (!validate()) return;

    const data = {
      shop: shopId,
      promotionCode: promotionCode.trim(),
      type,
      condition,
      percent: parseFloat(percent),
      maximumPromotion: maximumPromotion === "" ? undefined : parseFloat(maximumPromotion),
      useLimitCount: useLimitCount === "" ? undefined : parseInt(useLimitCount, 10),
      from: from || undefined,
      to: to || undefined,
    };

    setSaving(true);
    const run = isEdit
      ? updatePromotion({ variables: { data, promotionId } })
      : createPromotion({ variables: { data } });

    run
      .then(() => {
        window.location.href = `${endPoint}/superAdmin/promotions`;
      })
      .catch((err) => {
        setSaving(false);
        setFormError(readableError(err));
      });
  };

  if (isEdit && loadingPromotion) {
    return (
      <div className="page superShopsPage">
        <main className="main">
          <PageHeader title="Edit promotion" />
          <Skeleton height={320} />
        </main>
      </div>
    );
  }

  if (isEdit && promotionError) return <ErrorPage />;

  return (
    <div className="page superShopsPage">
      <main className="main">
        <PageHeader
          title={isEdit ? "Edit shop promotion" : "New shop promotion"}
          description="A discount code customers can apply to an order at one shop."
        />

        <Panel title="Promotion">
          <form className="superShopForm" onSubmit={onSubmit} style={{ width: "100%" }}>
            {formError && (
              <div className="dsFormMessage error" role="alert">
                {formError}
              </div>
            )}

            <Field
              label="Shop"
              error={errors.shop}
              required
              hint="Search by name, then pick the shop from the list."
            >
              <SearchField
                placeholder="Search shops by name..."
                value={shopSearch}
                onChange={(e) => setShopSearch(e.target.value)}
              />
            </Field>

            <FilterDropdown
              label={selectedShopLabel ? `Shop: ${selectedShopLabel}` : "Choose a shop"}
              options={
                shopOptions.length ? shopOptions : [{ value: "", label: "No matches — try another name" }]
              }
              value={shopId}
              onChange={(e) => setShopId(e.target.value)}
            />

            <div className="superShopFormRow">
              <Field label="Promotion code" htmlFor="promotionCode" error={errors.promotionCode} required>
                <input
                  id="promotionCode"
                  className="dsInput"
                  type="text"
                  value={promotionCode}
                  onChange={(e) => setPromotionCode(e.target.value.toUpperCase())}
                  placeholder="SUMMER25"
                />
              </Field>

              <Field label="Use limit" htmlFor="useLimitCount" error={errors.useLimitCount} optional>
                <input
                  id="useLimitCount"
                  className="dsInput"
                  type="number"
                  min="1"
                  value={useLimitCount}
                  onChange={(e) => setUseLimitCount(e.target.value)}
                  placeholder="Unlimited"
                />
              </Field>
            </div>

            <div className="superShopFormRow">
              <Field label="Discount type">
                <FilterDropdown
                  label={TYPE_LABELS[type]}
                  options={TYPE_OPTIONS}
                  value={type}
                  active={false}
                  onChange={(e) => setType(e.target.value)}
                />
              </Field>

              <Field label="Condition">
                <FilterDropdown
                  label={CONDITION_LABELS[condition]}
                  options={CONDITION_OPTIONS}
                  value={condition}
                  active={false}
                  onChange={(e) => setCondition(e.target.value)}
                />
              </Field>
            </div>

            <div className="superShopFormRow">
              <Field
                label={type === "PERCENT" ? "Percentage off" : "Amount off"}
                htmlFor="percent"
                error={errors.percent}
                required
              >
                <input
                  id="percent"
                  className="dsInput"
                  type="number"
                  min="0"
                  step="0.01"
                  value={percent}
                  onChange={(e) => setPercent(e.target.value)}
                  placeholder={type === "PERCENT" ? "25" : "10.00"}
                />
              </Field>

              <Field
                label="Maximum discount"
                htmlFor="maximumPromotion"
                optional
                hint="Caps how much a percentage code can take off."
              >
                <input
                  id="maximumPromotion"
                  className="dsInput"
                  type="number"
                  min="0"
                  step="0.01"
                  value={maximumPromotion}
                  onChange={(e) => setMaximumPromotion(e.target.value)}
                  placeholder="No cap"
                />
              </Field>
            </div>

            <div className="superShopFormRow">
              <Field label="Valid from" htmlFor="from" optional>
                <input
                  id="from"
                  className="dsInput"
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </Field>

              <Field label="Valid until" htmlFor="to" error={errors.to} optional>
                <input
                  id="to"
                  className="dsInput"
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
              </Field>
            </div>

            <div className="superShopFooter">
              <Button
                variant="secondary"
                onClick={() => (window.location.href = `${endPoint}/superAdmin/promotions`)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={saving}>
                {isEdit ? "Save changes" : "Create promotion"}
              </Button>
            </div>
          </form>
        </Panel>
      </main>
    </div>
  );
};

export default ShopPromotionForm;
